import { useLiveQuery } from 'dexie-react-hooks'
import { getProfile } from '../db/auth'
import { currentAccountId, unreadCount } from '../db/notifications'
import { navigate, type Route } from '../lib/router'
import { Icon } from './Icon'
import './AppHeader.css'

/** Le nom de chaque destination, tel qu'il s'écrit en tête d'écran. */
const TITLES: Record<string, string> = {
  '/transactions': 'Opérations',
  '/budget': 'Budget',
  '/profil': 'Profil',
}

/**
 * L'en-tête de l'application — une seule barre, deux formes.
 *
 * **Sur l'accueil** : la salutation, la cloche des notifications et l'accès au
 * profil. C'est la racine, on n'en revient pas.
 *
 * **Partout ailleurs** : un retour, et le nom de l'écran au milieu. Ni cloche
 * ni avatar — les répéter sur chaque onglet ferait trois portes vers le même
 * endroit à l'écran, et diluerait le repère qu'ils constituent sur l'accueil.
 *
 * ⚠️ **Le retour ramène à l'accueil, il ne défait pas l'historique.** Ces
 * quatre écrans sont des onglets, pas un empilement : on n'« entre » pas dans
 * Budget depuis un parent. Un `history.back()` renverrait vers l'onglet
 * précédent — donc n'importe lequel — et, sur une app ouverte directement sur
 * /budget, sortirait de l'application. L'accueil est la seule destination qui
 * ne surprenne jamais.
 */
export function AppHeader(
  { route, onNotifications }: { route: Route; onNotifications: () => void },
) {
  const profile = getProfile()
  // Par COMPTE et non par portefeuille : une notification s'adresse à une
  // personne. Sans compte connu, il n'y a pas de boîte — et donc rien à
  // compter.
  const accountId = currentAccountId()
  const unread = useLiveQuery(
    async () => (accountId ? unreadCount(accountId) : 0), [accountId], 0)

  if (route !== '/') {
    return (
      <header className="app-header is-inner">
        <button type="button" className="app-header-back"
                aria-label="Revenir à l'accueil"
                onClick={() => navigate('/')}>
          <Icon name="arrowLeft" size={20} />
        </button>
        <h1>{TITLES[route] ?? 'Ariariko'}</h1>
        {/* Une case vide en face du retour : sans elle le titre se décale de la
            largeur du bouton, et ne serait centré sur rien. */}
        <span aria-hidden="true" />
      </header>
    )
  }

  const firstName = profile?.first_name?.trim()

  return (
    <header className="app-header">
      <p className="app-header-hello">
        {firstName ? `Bonjour, ${firstName}` : 'Bonjour'}
        {/* Décoratif : un lecteur d'écran lirait « main qui salue » au milieu
            d'une phrase. */}
        <span aria-hidden="true"> 👋</span>
      </p>

      <div className="app-header-actions">
        <button type="button" className="app-header-bell"
                aria-label={unread > 0
                  ? `Notifications, ${unread} non lue${unread > 1 ? 's' : ''}`
                  : 'Notifications'}
                onClick={onNotifications}>
          <Icon name="bell" size={20} />
          {unread > 0 && (
            // Le compte est DANS le libellé du bouton ci-dessus : ici il n'est
            // que peint, sinon il serait annoncé deux fois.
            <span className="app-header-badge" aria-hidden="true">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>

        <button type="button" className="app-header-avatar"
                aria-label="Ouvrir mon profil"
                onClick={() => navigate('/profil')}>
          <span aria-hidden="true">{(firstName?.[0] ?? '?').toUpperCase()}</span>
        </button>
      </div>
    </header>
  )
}
